import React from 'react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  LayoutDashboard, 
  DollarSign, 
  CreditCard, 
  PieChart, 
  Users, 
  Activity,
  Newspaper,
  Video,
  Settings,
  ChevronLeft,
  ChevronRight,
  Home,
  LogOut
} from 'lucide-react';
import { NavLink, useNavigate } from 'react-router-dom';

const AdminSidebar = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const [userroles, setUserroles] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ OPTIMIZED: Get user role directly from localStorage (NO API CALL)
  useEffect(() => {
    const getUserRoleFromStorage = () => {
      try {
        // Get role from localStorage (set during login)
        const storedRole = localStorage.getItem('userRole');
        const token = localStorage.getItem('token');
        
        // Check if user is authenticated
        if (!token) {
          console.log('No token found - redirecting to login');
          navigate('/');
          return;
        }
        
        // Set user role from localStorage
        if (storedRole) {
          setUserroles(storedRole);
          console.log('✅ AdminSidebar - User role from localStorage:', storedRole);
        } else {
          console.warn('No userRole found in localStorage');
          // Fallback: try to get from user object
          const user = localStorage.getItem('user');
          if (user) {
            try {
              const userData = JSON.parse(user);
              if (userData.role) {
                setUserroles(userData.role);
                localStorage.setItem('userRole', userData.role);
              }
            } catch (e) {
              console.error('Error parsing user data:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error reading from localStorage:', error);
      } finally {
        setLoading(false);
      }
    };
    
    getUserRoleFromStorage();
  }, [navigate]);

  // Menu items for Admin
  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Traders', icon: DollarSign, path: '/admin/viewtrader' },
    { name: 'Inspections', icon: CreditCard, path: '/admin/viewinspection' },
    { name: 'Inspectors', icon: PieChart, path: '/admin/viewinspectors' },
    { name: 'File', icon: Users, path: '/admin/viewfile' },
    { name: 'Compliance', icon: Activity, path: '/admin/ViewCompliance' },
    { name: 'Users', icon: Newspaper, path: '/admin/viewuser' },
  ];

  // Menu items for Inspectors
  const Inspectormenu = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Traders', icon: DollarSign, path: '/admin/InspectorViewtrader' },
    { name: 'Inspections', icon: CreditCard, path: '/admin/inspectorviewinspection' },
    { name: 'Myfile', icon: Users, path: '/admin/InspectorViewfile' },
    { name: 'Compliances', icon: Activity, path: '/admin/insepectorcomliance' },
  ];

  // Menu items for regular user
  const Usermenu = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'My File', icon: Users, path: '/admin/userviewfile' },
    { name: 'Compliance', icon: Activity, path: '/admin/UserCompliance' },
  ];

  const Tradermenu = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Myinspections', icon: CreditCard, path: '/admin/traderinspection' },
    { name: 'My File', icon: Users, path: '/admin/userviewfile' },
    { name: 'Compliance', icon: Activity, path: '/admin/UserCompliance' },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading...</h3>
        </div>
      </div>
    );
  }

  // Select menu based on role
  let activeMenu = Usermenu; // default
  if (userroles === 'admin') {
    activeMenu = menuItems;
  } else if (userroles === 'inspector') {
    activeMenu = Inspectormenu;
  } else if (userroles === 'trader') {
    activeMenu = Tradermenu;
  }

  console.log('Rendering AdminSidebar with user role:', userroles);
  console.log('Active menu items:', activeMenu.length);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userroles');
    navigate('/');
  };

  return (
    <aside className={`fixed left-0 top-0 h-full bg-indigo-700 text-white shadow-xl transition-all duration-300 z-30 ${collapsed ? 'w-20' : 'w-64'}`}>
      {/* Logo Section */}
      <div className="flex items-center justify-between p-6 border-b border-indigo-600">
        <div className={`flex items-center gap-3 ${collapsed ? 'justify-center w-full' : ''}`}>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="text-xl font-bold text-white">ETIMS</h1>
              <p className="text-xs text-indigo-200 capitalize">{userroles} Portal</p>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-indigo-600 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
        )}
        {collapsed && (
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-indigo-600 transition-colors mt-2"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        )}
      </div>

      {/* Role Badge (only when expanded) */}
      {!collapsed && (
        <div className="mx-4 mt-4 p-2 bg-indigo-600/50 rounded-lg">
          <p className="text-xs text-indigo-200">Logged in as:</p>
          <p className="text-sm font-semibold capitalize">{userroles || 'User'}</p>
        </div>
      )}

      {/* Menu Items */}
      <nav className="p-4 flex-1 overflow-y-auto h-[calc(100%-180px)]">
        <ul className="space-y-2">
          {activeMenu.map((item) => (
            <li key={item.name}>
              <NavLink
                to={item.path}
                className={({ isActive }) => 
                  `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-500 text-white shadow-lg'
                      : 'text-indigo-100 hover:bg-indigo-600 hover:text-white'
                  } ${collapsed ? 'justify-center' : ''}`
                }
                end
              >
                <item.icon className="w-5 h-5" />
                {!collapsed && <span>{item.name}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section - Logout */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-indigo-600">
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full text-indigo-100 hover:bg-red-600/20 hover:text-red-300 ${
            collapsed ? 'justify-center' : ''
          }`}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;